import { cx } from '@emotion/css';
import {
  Node as ReactFlowNode,
  NodeProps,
  NodeResizer,
  Position,
  useInternalNode,
} from '@xyflow/react';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useModalState } from '@/hooks/useModalState';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';
import { COLORS } from '@/styles/tokens/colors';

import { Handle, HandleProps } from '../../../components/Handle';
import { Tile, TileProps } from '../../../components/Tile';
import { useFlowChartContext } from '../../context/flowChart.context';
import { useCursorStyles } from '../../hooks/useCursorStyles';
import { useDraggingNode } from '../../hooks/useDraggingNode';
import { useIsInteractive } from '../../hooks/useIsInteractive';
import { useResizeAllSelectedNodes } from '../../hooks/useResizeAllSelectedNodes';
import { useSelectedNodes } from '../../hooks/useSelectedNodes';
import {
  SectionLabelNodeChildAttrs,
  TileNodeAdditionalProps,
} from '../../types';
import {
  NODE_RESIZER_HANDLE_STYLE,
  NODE_RESIZER_LINE_STYLE,
} from '../nodeResizerStyles';
import { TileEditModal } from './TileEditModal';
import { DeleteTileControlButton } from './TileNodeControls/DeleteTileControlButton';
import { TileNodeControls } from './TileNodeControls/TileNodeControls';
import { TileSettingsControlButton } from './TileNodeControls/TileSettingsControlButton';
import { useTileNodeSlots } from './tileNodeSlots.context';

// Query parameter name for debugging dimensions
export const DEBUG_TILE_DIMENSIONS_PARAM = 'debug';

//  isSelected is omitted because we compute it based on internal react flow props in this component
export type TileNodeData = Record<string, unknown> &
  Omit<TileProps, 'isSelected' | 'isIconVariant'> &
  SectionLabelNodeChildAttrs &
  TileNodeAdditionalProps;

export type TileNodeProps = NodeProps<ReactFlowNode<TileNodeData>>;

const AllPositions = Object.values(Position);

const defaultEditModal: NonNullable<TileNodeData['editModal']> = (props) => {
  return <TileEditModal {...props} />;
};

/**
 * Hook to check if debug dimensions should be shown via query parameter
 * @returns boolean indicating if debug dimensions should be shown
 */
function useDebugTileDimensions(defaultValue = false): boolean {
  const [searchParams] = useSearchParams();
  const paramValue = searchParams.get(DEBUG_TILE_DIMENSIONS_PARAM);

  // Show debug dimensions if the param is present and not explicitly set to 'false'
  return paramValue !== null ? paramValue === 'true' : defaultValue;
}

/**
 * Debug component that displays the dimensions of a tile node
 */
function TileNodeDimensionsDebug({
  width,
  height,
  selected,
}: {
  width?: number;
  height?: number;
  selected?: boolean;
}) {
  // Format dimensions for display
  const dimensions = `${Math.round(width || 0)} × ${Math.round(height || 0)}`;

  const debugBorderStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: '1px dashed rgba(255, 0, 0, 0.5)',
    borderRadius: '4px',
    pointerEvents: 'none',
    zIndex: 10,
  } as const;

  const dimensionsLabelStyle = {
    position: 'absolute',
    top: 2,
    right: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '2px',
    padding: '2px 4px',
    fontSize: '10px',
    lineHeight: 1,
    fontFamily: 'monospace',
    color: COLORS.GRAY[900],
    pointerEvents: 'none',
    zIndex: 20,
    border: `1px solid ${selected ? COLORS.BLUE[500] : COLORS.GRAY[400]}`,
  } as const;

  return (
    <>
      <div style={debugBorderStyle} />
      <div style={dimensionsLabelStyle}>{dimensions}</div>
    </>
  );
}

export function TileNodeBase({
  id,
  data: {
    controls,
    editModal = defaultEditModal,
    initiallyOpenEditModal = false,
    classes: tileClasses = {},
    fontSizeVariant,
    componentType = GraphVizNodeComponentType.Tile,
    iconShape,
    tileEmphasis,
    dimensionsFinalized,
    hideNodeResizer = false,
    hideNodeControls = false,
    disableDoubleClickEdit = false,
    onNodeResizeStart,
    onNodeResizeEnd,
    onDelete,
    ...tileProps
  },
  selected,
  isConnectable,
  dragging,
  width,
  height,
}: TileNodeProps) {
  const internalNode = useInternalNode(id);
  const { root, ...classes } = tileClasses;
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState(
    initiallyOpenEditModal
  );
  const { dropTargetNode } = useFlowChartContext();
  const { cursorClassName } = useCursorStyles(id);
  const { draggingNode } = useDraggingNode();
  const { isInteractive } = useIsInteractive();
  const { selectedNodes } = useSelectedNodes();
  const commonHandleProps: Partial<HandleProps> = {
    isConnectable,
    hidden: true,
  };
  const isGrouped = id === dropTargetNode?.id && !!draggingNode;
  const resizeHandlesVisible = !!selected && isInteractive && !dragging;
  const nodeControlsVisible = resizeHandlesVisible && selectedNodes.length <= 1;
  const isIconVariant = componentType === GraphVizNodeComponentType.TileIcon;

  // Check if debug dimensions should be shown from URL query parameter or prop
  const showDebugFromUrl = useDebugTileDimensions();
  const shouldShowDebug = showDebugFromUrl;

  const onResize = useResizeAllSelectedNodes(id);
  const canEditHouseholdData = useCanEditHouseholdData();

  const handleTileDoubleClick = useCallback<
    NonNullable<TileProps['onDoubleClick']>
  >(
    (event) => {
      // The tile's children can render modals, which portal their DOM out of
      // the tile but still bubble synthetic events up the React tree. Only
      // treat double-clicks whose DOM target is physically inside the tile as
      // an edit gesture; otherwise e.g. rapid clicks on a modal's save button
      // would open this tile's edit modal on top of it.
      const targetIsInsideTile =
        event.target instanceof Node &&
        event.currentTarget.contains(event.target);
      if (!targetIsInsideTile) return;

      if (!isInteractive) return;
      if (disableDoubleClickEdit) return;

      // Check if user has permission to edit household data
      if (!canEditHouseholdData) {
        return; // Don't open edit modal for users without edit permissions
      }

      // Open the edit modal on double-click
      openModal();
    },
    [isInteractive, disableDoubleClickEdit, openModal, canEditHouseholdData]
  );

  return (
    <>
      {isInteractive &&
        isModalOpen &&
        editModal?.({ isOpen: isModalOpen, onClose: closeModal })}
      {!hideNodeResizer && (
        <NodeResizer
          nodeId={id}
          lineStyle={NODE_RESIZER_LINE_STYLE}
          handleStyle={NODE_RESIZER_HANDLE_STYLE}
          isVisible={resizeHandlesVisible}
          onResizeStart={() => onNodeResizeStart?.(id)}
          onResize={onResize}
          onResizeEnd={() => onNodeResizeEnd?.(id)}
        />
      )}
      {!hideNodeControls && (
        <TileNodeControls isVisible={nodeControlsVisible}>
          {controls ?? <TileSettingsControlButton onClick={openModal} />}
          {onDelete && <DeleteTileControlButton onClick={() => onDelete(id)} />}
        </TileNodeControls>
      )}
      <Tile
        classes={{
          root: cx(cursorClassName, root),
          ...classes,
        }}
        width={width}
        // Measured height is an output, not a fixed constraint. Keep automatic
        // tiles content-sized when their labels wrap at the measured width.
        height={internalNode?.height}
        dragging={dragging}
        isSelected={dragging || !!selected}
        isGrouped={isGrouped}
        fontSizeVariant={fontSizeVariant}
        isIconVariant={isIconVariant}
        iconShape={iconShape}
        tileEmphasis={tileEmphasis}
        dimensionsFinalized={dimensionsFinalized}
        onDoubleClick={handleTileDoubleClick}
        {...tileProps}
      />
      {shouldShowDebug && (
        <TileNodeDimensionsDebug
          width={width}
          height={height}
          selected={selected}
        />
      )}
      {/* Build source/target handles for each four corners of the tile square */}
      {AllPositions.map((position) => (
        <Handle
          key={`target-${position}`}
          type="target"
          {...commonHandleProps}
          id={position}
          position={position}
        />
      ))}
      {/* Build source/target handles for each four corners of the tile square */}
      {AllPositions.map((position) => (
        <Handle
          key={`source-${position}`}
          type="source"
          {...commonHandleProps}
          id={position}
          position={position}
        />
      ))}
    </>
  );
}

/**
 * Slot-aware tile node registered as the `tile` node type. A host can inject a
 * replacement via {@link TileNodeSlotsProvider} (e.g. the presentation designer
 * wraps entity tiles with hover + an edit toolbar); with no provider in scope
 * this is exactly `TileNodeBase`.
 */
export function TileNode(props: TileNodeProps) {
  const { Tile: SlotTile } = useTileNodeSlots();
  const Component = SlotTile ?? TileNodeBase;
  return <Component {...props} />;
}
